import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ref, onValue, remove, update, get } from "firebase/database";
import { db } from "../../configs/Firebase";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Button from "../../designSystem/b-molecules/buttons/Button";
import EditThemeDialog from "./components/EditThemeDialog";
import style from "./UploadDetail.module.css";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import { canEditSection, SECTIONS } from "../../utils/permissions";

const UploadDetail = () => {
  const { uploadId } = useParams();
  const navigate = useNavigate();
  const org = useSelector((state) => state.orgStatus.value);
  const userStatus = useSelector((state) => state.userStatus.value);
  const [upload, setUpload] = useState(null);
  const [records, setRecords] = useState([]);
  const [allThemes, setAllThemes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [user, setUser] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const scrollPositionRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);

  // Check user permissions
  useEffect(() => {
    if (userStatus) {
      setUser(JSON.parse(userStatus.payload));
    }
  }, [userStatus]);

  useEffect(() => {
    if (user && org.payload) {
      setCanEdit(canEditSection(user, org.payload, SECTIONS.CUSTOMERS));
    } else {
      setCanEdit(false);
    }
  }, [user, org.payload]);

  // Load upload, brands, countries, and themes
  useEffect(() => {
    if (org.payload && uploadId) {
      // Load specific upload
      const uploadRef = ref(db, `userData/${org.payload}/customers/data/${uploadId}`);
      const uploadUnsubscribe = onValue(uploadRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setUpload({ id: uploadId, ...data });
        } else {
          setUpload(null);
        }
      });

      // Load brands
      const brandsRef = ref(db, `userData/${org.payload}/brands`);
      const brandsUnsubscribe = onValue(brandsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const brandsList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setBrands(brandsList);
        } else {
          setBrands([]);
        }
      });

      // Load countries
      const countriesRef = ref(db, `userData/${org.payload}/countries`);
      const countriesUnsubscribe = onValue(countriesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const countriesList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setCountries(countriesList);
        } else {
          setCountries([]);
        }
      });

      // Load all themes for the edit dialog
      const themesRef = ref(db, `userData/${org.payload}/customers/themes`);
      const themesUnsubscribe = onValue(themesRef, (snapshot) => {
        const themes = snapshot.val();
        if (themes) {
          const themesList = Object.keys(themes).map(id => ({
            id,
            ...themes[id]
          }));
          setAllThemes(themesList);
        } else {
          setAllThemes([]);
        }
      });

      // Cleanup function to unsubscribe from all listeners
      return () => {
        uploadUnsubscribe();
        brandsUnsubscribe();
        countriesUnsubscribe();
        themesUnsubscribe();
      };
    }
  }, [org.payload, uploadId]);

  // Extract records from upload
  useEffect(() => {
    if (upload && upload.records) {
      let recordsArray = [];

      // Handle both array and object formats
      if (Array.isArray(upload.records)) {
        recordsArray = upload.records.map(record => ({
          ...record,
          uploadId: upload.id
        }));
      } else if (typeof upload.records === 'object') {
        // Convert object to array and attach uploadId
        recordsArray = Object.keys(upload.records).map(id => ({
          id,
          ...upload.records[id],
          uploadId: upload.id
        }));
      }

      // Filter out uncategorized records
      const categorizedRecords = recordsArray.filter(record => {
        const themeId = record.theme || 'uncategorized';
        const themeName = record.themeName || 'Uncategorized';
        const isUncategorized = themeId === 'uncategorized' || themeName === 'Uncategorized' || !record.theme;
        return !isUncategorized;
      });

      // Sort by date (newest first)
      categorizedRecords.sort((a, b) => {
        try {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          if (isNaN(dateA)) return 1;
          if (isNaN(dateB)) return -1;
          return dateB - dateA;
        } catch {
          return 0;
        }
      });

      setRecords(categorizedRecords);

      // Restore scroll position after data update if needed
      if (shouldRestoreScrollRef.current) {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPositionRef.current);
          shouldRestoreScrollRef.current = false;
        });
      }
    } else {
      setRecords([]);
    }
  }, [upload]);

  function handleEditRecord(record) {
    // Store current scroll position before opening dialog
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    shouldRestoreScrollRef.current = true;
    setSelectedRecord(record);
    setShowEditDialog(true);
  }

  function handleThemeUpdated() {
    // Data will automatically update via Firebase listeners
    // Scroll will be restored in useEffect after records update
  }

  async function handleDeleteUpload() {
    if (!window.confirm(
      `Are you sure you want to delete this upload and all ${upload?.recordCount || records.length} records? This cannot be undone.`
    )) return;

    for (const record of records) {
      if (record.personaId) {
        const personaRef = ref(db, `userData/${org.payload}/personas/${record.personaId}`);
        const snapshot = await get(personaRef);
        const personaData = snapshot.val();
        if (personaData?.linkedFeedback) {
          const cleaned = personaData.linkedFeedback.filter(
            f => !(f.feedbackId === record.id && f.uploadId === uploadId)
          );
          await update(personaRef, { linkedFeedback: cleaned });
        }
      }
    }

    await remove(ref(db, `userData/${org.payload}/customers/data/${uploadId}`));
    navigate("/customers");
  }

  // Get brand name
  const getBrandName = () => {
    if (!upload || !upload.brand) return null;
    const brand = brands.find(b => b.id === upload.brand);
    return brand ? brand.name : null;
  };

  // Get country name
  const getCountryName = () => {
    if (!upload || !upload.country) return null;
    const country = countries.find(c => c.id === upload.country);
    return country ? country.name : null;
  };

  // Format source name
  const getSourceName = () => {
    if (!upload || !upload.source) return "Unknown";
    return upload.source.replace("_", " ");
  };

  return (
    <div className="mainContentInner">
      <TitleBar
        title={upload ? getSourceName() : "Loading..."}
        actions={[
          {
            icon: "arrow_back",
            name: "Back",
            action: () => navigate("/customers"),
            variants: ["text"]
          },
          ...(canEdit ? [{
            icon: "delete",
            name: "Delete",
            action: handleDeleteUpload,
            variants: ["text"]
          }] : [])
        ]}
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Customers", url: "/customers" }
        ]}
      />

      <div className="contentPadding">
        {upload && (
          <>
            {/* Upload Metadata */}
            <div className={style.uploadMetadata}>
              <div className={style.metadataRow}>
                <div className={style.metadataItem}>
                  <Icon>source</Icon>
                  <div>
                    <div className={style.metadataLabel}>Source</div>
                    <div className={style.metadataValue}>{getSourceName()}</div>
                  </div>
                </div>
                <div className={style.metadataItem}>
                  <Icon>calendar_today</Icon>
                  <div>
                    <div className={style.metadataLabel}>Upload Date</div>
                    <div className={style.metadataValue}>
                      {upload.uploadedAt
                        ? new Date(upload.uploadedAt).toLocaleDateString()
                        : "Unknown"}
                    </div>
                  </div>
                </div>
                <div className={style.metadataItem}>
                  <Icon>description</Icon>
                  <div>
                    <div className={style.metadataLabel}>Records</div>
                    <div className={style.metadataValue}>{upload.recordCount || records.length || 0}</div>
                  </div>
                </div>
                {getBrandName() && (
                  <div className={style.metadataItem}>
                    <Icon>label</Icon>
                    <div>
                      <div className={style.metadataLabel}>Brand</div>
                      <div className={style.metadataValue}>{getBrandName()}</div>
                    </div>
                  </div>
                )}
                {getCountryName() && (
                  <div className={style.metadataItem}>
                    <Icon>public</Icon>
                    <div>
                      <div className={style.metadataLabel}>Country</div>
                      <div className={style.metadataValue}>{getCountryName()}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Records List */}
            <div className={style.recordsList}>
              <h3>Feedback Items ({records.length})</h3>
              <div className={style.records}>
                {records.length > 0 ? (
                  records.map((record, index) => (
                    <div key={record.id || index} className={style.recordItem}>
                      <div className={style.recordContent}>
                        {record.feedbackText && (
                          <div className={style.recordText}>{record.feedbackText}</div>
                        )}
                        <div className={style.recordMeta}>
                          {record.date && (
                            <span className={style.recordDate}>
                              <Icon>calendar_today</Icon> {new Date(record.date).toLocaleDateString()}
                            </span>
                          )}
                          {record.themeName && (
                            <span className={style.recordTheme}>
                              <Icon>category</Icon> {record.themeName}
                            </span>
                          )}
                          {record.sentiment && (
                            <span className={`${style.recordSentiment} ${style[record.sentiment]}`}>
                              <Icon variants={["inheritColor"]}>
                                {record.sentiment === 'positive' ? 'sentiment_satisfied' :
                                  record.sentiment === 'negative' ? 'sentiment_dissatisfied' :
                                    'sentiment_neutral'}
                              </Icon>
                            </span>
                          )}
                          {record.rating && (
                            <span className={style.recordRating}>
                              <Icon>star</Icon> {record.rating}
                            </span>
                          )}
                          {canEdit && (
                            <div className={style.recordActions}>
                              <Button
                                variants={["text", "small"]}
                                onClick={() => handleEditRecord(record)}
                              >
                                <Icon>edit</Icon>
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  ))
                ) : (
                  <div className={style.noRecords}>No feedback records found in this upload.</div>
                )}
              </div>
            </div>
          </>
        )}

        {!upload && (
          <div className={style.loading}>Loading upload details...</div>
        )}
      </div>

      <EditThemeDialog
        show={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
        themes={allThemes}
        orgId={org.payload}
        uploadId={uploadId}
        onThemeUpdated={handleThemeUpdated}
      />
    </div>
  );
};

export default UploadDetail;

